// ⚡🧪 THE CORE PILL — the POG'CAPACITOR, visible from the kingdom map itself.
// Tap to expand the full meter. Also runs the STRIKE SKY SHOW (golden lightning at 9,000)
// and the CAPACITOR COUNTDOWN hype toasts as the core nears the strike.
import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { PogCapacitor } from "@/components/PogCapacitor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CD_THRESHOLDS = [10, 25, 50, 100, 200, 300];

export const CapacitorPill = () => {
  const [cap, setCap] = useState(null);
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [hall, setHall] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/capacitor/hall`).then((r) => setHall(r.data.hall || [])).catch(() => setHall([]));
  }, [open]);

  useEffect(() => {
    let alive = true;
    const onCap = (c) => {
      if (!alive) return;
      setCap(c);
      if (c.striking) {
        const k = `airpog_strike_seen_${c.strikes}`;
        if (!localStorage.getItem(k)) {
          localStorage.setItem(k, "1");
          setShow(true);
          setTimeout(() => { if (alive) setShow(false); }, 9000);
        }
      } else {
        const remaining = c.goal - c.charge;
        const th = CD_THRESHOLDS.find((t) => remaining <= t);
        if (th && remaining > 0) {
          const k = `airpog_cap_cd_${c.strikes}_${th}`;
          if (!localStorage.getItem(k)) {
            localStorage.setItem(k, "1");
            toast(`⚡ THE CORE ROARS — only ${remaining.toLocaleString()} POGAWATTS to the STRIKE!`, { duration: 6000 });
          }
        }
      }
    };
    const pull = () => axios.get(`${API}/capacitor`, { withCredentials: true })
      .then((r) => onCap(r.data)).catch(() => {});
    pull();
    const t = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!cap) return null;
  const pct = Math.min(100, (cap.charge / cap.goal) * 100);

  const claimStrike = async () => {
    try {
      const r = await axios.post(`${API}/capacitor/claim`, {}, { withCredentials: true });
      if (r.data.ok) { toast.success(`⚡ +${r.data.bonus} POG BUCKS from the lightning!`); setCap((c) => ({ ...c, claimed: true })); }
      else toast(r.data.reason || "");
    } catch { toast("Sign in to catch the lightning ⚡"); }
  };

  return (
    <>
      <button
        data-testid="capacitor-pill"
        onClick={() => setOpen((o) => !o)}
        className={`absolute left-3 top-32 z-[1141] flex items-center gap-1.5 rounded-full pl-2 pr-2.5 py-1.5 border backdrop-blur-md shadow-lg active:scale-95 transition-transform ${cap.striking ? "border-yellow-300 shadow-[0_0_16px_rgba(250,204,21,0.6)]" : "border-fuchsia-400/60 shadow-[0_0_12px_rgba(217,70,239,0.35)]"}`}
        style={{ background: "rgba(12,8,24,0.82)" }}
        title="THE POG'CAPACITOR — community charge meter"
      >
        <span className={`text-[13px] ${cap.striking ? "animate-pulse" : ""}`}>⚡</span>
        <span className="flex flex-col items-start leading-none">
          <span className="text-[8px] font-heading font-black tracking-widest text-fuchsia-300">THE CORE</span>
          <span className="mt-0.5 w-14 h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <span className={`block h-full rounded-full ${cap.striking ? "bg-yellow-400 animate-pulse" : "bg-gradient-to-r from-fuchsia-500 to-yellow-400"}`} style={{ width: `${Math.max(3, pct)}%` }} />
          </span>
        </span>
        <span className={`text-[9px] font-black ${cap.striking ? "text-yellow-300" : "text-fuchsia-200"}`}>
          {cap.striking ? "STRIKE!" : cap.charge.toLocaleString()}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="capacitor-pill-panel"
            initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
            className="absolute left-3 top-[11.2rem] z-[1142] w-[min(88vw,320px)] rounded-2xl shadow-2xl"
            style={{ background: "rgba(10,7,20,0.94)" }}
          >
            <button data-testid="capacitor-pill-close" onClick={() => setOpen(false)}
              className="absolute -top-2 -right-2 z-10 w-6 h-6 rounded-full bg-slate-800 border border-white/25 text-white text-[11px] font-black">✕</button>
            <PogCapacitor />
            {hall && hall.length > 0 && (
              <div data-testid="strike-hall" className="px-4 pb-4 -mt-1">
                <p className="text-[9px] font-heading font-black tracking-[0.25em] text-yellow-300">⚡ SCROLL OF HONOR</p>
                {hall.map((h) => (
                  <p key={h.strike_n} className="mt-1 text-[10px] font-bold text-slate-300 leading-snug">
                    <span className="text-yellow-200 font-black">STRIKE #{h.strike_n}</span> · {h.catchers} caught the lightning — <span className="text-yellow-300">👑 {h.names[0]}</span>{h.names.length > 1 ? `, ${h.names.slice(1).join(", ")}` : ""}{h.catchers > h.names.length ? "…" : ""}
                  </p>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {/* ⚡🌩️ STRIKE SKY SHOW — golden lightning floods the map at 9,000 POGAWATTS */}
      {show && (
        <div data-testid="strike-sky-show" className="fixed inset-0 z-[3000] pointer-events-none overflow-hidden">
          <style>{"@keyframes skyflash{0%,100%{opacity:0}50%{opacity:1}}@keyframes boltpop{0%{opacity:0}10%{opacity:1}60%{opacity:1}100%{opacity:0}}"}</style>
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 0%, rgba(250,204,21,0.5), rgba(250,204,21,0.12) 45%, transparent 70%)", animation: "skyflash 1.4s ease-in-out 5" }} />
          {[8, 30, 52, 74, 90].map((x, i) => (
            <svg key={x} className="absolute top-0" style={{ left: `${x}%`, width: 90, height: 440, opacity: 0, animation: `boltpop 1s ease-out ${i * 0.4}s 4` }} viewBox="0 0 90 440">
              <path d="M48 0 L30 150 L52 160 L22 300 L44 310 L18 440" stroke="#fde047" strokeWidth="7" fill="none" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 12px rgba(250,204,21,0.95))" }} />
            </svg>
          ))}
          <div className="absolute top-[22%] left-1/2 -translate-x-1/2 w-full px-4 pointer-events-auto text-center">
            <p className="text-2xl sm:text-4xl font-heading font-black text-yellow-300 drop-shadow-[0_0_18px_rgba(250,204,21,0.9)] animate-pulse">⚡ 9,000 POGAWATTS ⚡</p>
            <p className="mt-1 text-sm font-heading font-black text-yellow-100 drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]">GOLDEN LIGHTNING STRIKES THE MAP — THE WHOLE KINGDOM SEES IT!</p>
            {!cap.claimed && (
              <button data-testid="strike-claim-btn" onClick={claimStrike}
                className="mt-3 px-5 h-10 rounded-full bg-yellow-400 text-slate-900 text-xs font-heading font-black shadow-[0_0_20px_rgba(250,204,21,0.8)] active:scale-95 transition-transform">
                ⚡ CATCH THE LIGHTNING +25
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
